import { useState } from 'react';

import { OverrideModal, type OverrideDiffField } from './override-modal';
import { ReevalTrayModal } from './reeval-tray-modal';
import { useRegisterEntryQuery } from '@/lib/api/registers';

/**
 * Bridge between the global `data-open-override` trigger and the
 * OverrideModal → ReevalTrayModal round-trip.
 *
 * The EditorModalHost owns modal state via its own `ActiveModal` union
 * (not the ModalProvider), so this bridge replicates the close-then-open
 * pattern from `useOverrideFlow` with local state: stage 'override'
 * until the capture commits, then stage 'tray' with the new override id.
 *
 * The trigger only carries ids (`data-open-override` = register entry
 * id, `data-register-type` = its register). The modal needs the entry's
 * payload — both for the header summary and, critically, for `newValue`:
 * `captureOverride` REWRITES the entry payload to `new_value`, so the
 * bridge must pass the current payload unchanged (a whole-entry flag,
 * not a field edit). Passing `{}` here would wipe the entry.
 */
export function OverrideHostBridge({
  engagementId,
  entryId,
  registerType,
  fieldId,
  onClose,
}: {
  engagementId: string;
  entryId: string;
  registerType: string;
  fieldId?: string;
  onClose: () => void;
}) {
  const [tray, setTray] = useState<{ overrideId: string; reasonTag: string } | null>(null);
  const entry = useRegisterEntryQuery(engagementId, registerType, entryId);

  if (tray) {
    return (
      <ReevalTrayModal
        engagementId={engagementId}
        triggerOverrideId={tray.overrideId}
        reasonTag={tray.reasonTag}
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      />
    );
  }

  if (entry.isError) {
    return (
      <div
        role="alert"
        className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-accent-red-300 bg-paper-0 px-4 py-3 text-sm text-accent-red-700 shadow-lg"
      >
        Could not load this entry for override —{' '}
        {entry.error instanceof Error ? entry.error.message : 'unknown error'}.
        <button type="button" className="ml-3 font-semibold underline" onClick={onClose}>
          Dismiss
        </button>
      </div>
    );
  }

  if (!entry.data) return null;

  const payload = (entry.data.payload ?? {}) as Record<string, unknown>;
  const title =
    firstString(payload, ['title', 'name', 'label', 'summary', 'description']) ??
    humanize(registerType);

  const diff: OverrideDiffField[] = [];
  if (fieldId) {
    const current = payload[fieldId];
    if (current !== undefined && current !== null) {
      diff.push({ label: humanize(fieldId), before: String(current), after: String(current) });
    }
  }

  return (
    <OverrideModal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      engagementId={engagementId}
      registerEntryId={entryId}
      fieldId={fieldId ?? null}
      sourceDocumentId={null}
      target={{
        shortId: `R-${entryId.slice(0, 4).toUpperCase()}`,
        title,
        domain: humanize(registerType),
        originalAiConfidence: entry.data.original_ai_confidence,
      }}
      diff={diff}
      // Whole-entry flag: captureOverride rewrites payload to newValue, so
      // pass the current payload untouched.
      newValue={payload}
      onSaved={(result) => {
        // Same beat as useOverrideFlow: let the first overlay unmount
        // before mounting the tray so the exit animation doesn't clash.
        globalThis.setTimeout(() => {
          setTray({ overrideId: result.id, reasonTag: result.reasonTag });
        }, 16);
      }}
    />
  );
}

function firstString(payload: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return undefined;
}

function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
